import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { money } from "../lib/format";
import { shareModeLabel } from "../lib/shareCard";
import { useSessionSummary } from "../state/useSessionSummary";
import { MilestoneBookNudge } from "./BookNudge";
import { avgLoss, avgWin, DEFAULT_BALANCE, useAccountStore } from "../state/accountStore";
import { isDialogOpen } from "../state/dialogStore";
import { usePanelStore } from "../state/panelStore";
import {
  canReview,
  RUN_KIND_LABEL,
  useHistoryStore,
  type HistoryEntry,
  type HistoryOutcome,
} from "../state/historyStore";
import { useSessionStore } from "../state/sessionStore";
import { Kbd } from "./Kbd";
import { formatRoundTime } from "./RevealBanner";
import { ShareButton } from "./ShareButton";
import { Tooltip } from "./Tooltip";

// Reopening a played round is a history-panel-only path, not needed to
// render a fresh one (GRA code-splitting pass), so it is its own chunk.
const ReviewDialog = lazy(() =>
  import("./ReviewDialog").then((m) => ({ default: m.ReviewDialog })),
);

/**
 * The browsable round history (GRA-123): a toggle reveals a capped,
 * scrolling, newest-first list of every completed Round across every Loop.
 * The session summary (GRA-138) is a sibling toggle, same visual language:
 * rounds/accuracy/streak/P&L for this sitting, derived from this same
 * history log so the numbers can never disagree with the entries below it.
 * "This sitting" is bounded by state/sessionStore's `startedAt` (page load,
 * or the last explicit reset); the strip states that boundary plainly rather
 * than leaving it implicit.
 *
 * Split into `HistoryToggles` (the two trigger buttons) and `HistoryPanels`
 * (what they open) so a caller can place the triggers in one row and the
 * panels wherever that row's other expansions land: every Loop view now
 * shares one utility row above the chart with settings and tools, in place
 * of the two separate rows this used to cost (GRA-148 comment in LoopShell).
 * The open/collapsed flags live in a module-level store, same reason
 * SettingsBar's `useSettingsPanel` does: every Loop view remounts this pair
 * with the row itself when the active Loop changes, and component state
 * would reset right along with it.
 *
 * A thin wrapper over the shared panelStore (GRA-148 follow-up): history and
 * session are two of the four slots that one open value covers, so opening
 * either closes whichever other panel (settings, tools, or the other of this
 * pair) was open.
 */

function useHistoryPanel(): {
  open: boolean;
  summaryOpen: boolean;
  toggle: () => void;
  toggleSummary: () => void;
} {
  const open = usePanelStore((s) => s.open === "history");
  const summaryOpen = usePanelStore((s) => s.open === "session");
  const toggle = usePanelStore((s) => s.toggle);
  // Stable callbacks: the keyboard effect in HistoryToggles depends on them.
  const toggleHistory = useCallback(() => toggle("history"), [toggle]);
  const toggleSession = useCallback(() => toggle("session"), [toggle]);
  return { open, summaryOpen, toggle: toggleHistory, toggleSummary: toggleSession };
}

/** Exposed for tests that need to reset the shared toggle state between cases. */
export { useHistoryPanel };

const OUTCOME_TEXT: Record<HistoryOutcome, string> = {
  correct: "Correct",
  incorrect: "Wrong",
  win: "Win",
  loss: "Loss",
  flat: "Flat",
};

/** Mirrors ResultBanner's precedent: correctness/profitability reads in the
 * candle-up / candle-down tokens, same as a Reveal banner's own verdict. */
const OUTCOME_COLOR: Record<HistoryOutcome, string> = {
  correct: "text-up",
  win: "text-up",
  incorrect: "text-down",
  loss: "text-down",
  flat: "text-muted",
};

function timeText(timestamp: number): string {
  return new Date(timestamp).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * One entry, laid out like a Reveal banner: content sized to itself and
 * wrapped with a gap (RevealBanner's own pattern), never a fixed pixel grid —
 * a grid of fixed columns is what crushed this into unreadable stacked
 * whitespace on a phone (GRA-123 review). The identity fields (when, which
 * Loop, which Instrument, the choice, the outcome) share one wrapping line;
 * the resolution detail and the Round's own dates each get their own line
 * since they read as sentences, not label/value pairs.
 */
function HistoryRow({
  entry,
  onReview,
}: {
  entry: HistoryEntry;
  onReview: (entry: HistoryEntry) => void;
}) {
  const loop = shareModeLabel(entry.mode, entry.timeframe, entry.horizon ?? "trend");
  return (
    <li className="flex flex-col gap-y-1 border-b border-muted/10 py-2 last:border-b-0">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-muted">{timeText(entry.timestamp)}</span>
        <span className="text-muted">{loop}</span>
        <span className="text-fg">{entry.instrumentName}</span>
        <span>{entry.choice}</span>
        <span className={`font-bold ${OUTCOME_COLOR[entry.outcome]}`}>
          {OUTCOME_TEXT[entry.outcome]}
        </span>
        <span className="text-dim">{RUN_KIND_LABEL[entry.kind]}</span>
        {/* Only an entry that names its Round can reopen it (GRA-154):
            everything logged before round ids were stored simply offers no
            Review, rather than an action that would fail when pressed. */}
        {canReview(entry) && (
          <button
            type="button"
            onClick={() => onReview(entry)}
            className="flex min-h-8 items-center text-accent hover:underline sm:min-h-6"
          >
            review
          </button>
        )}
      </div>
      <span className="text-muted">{entry.detail}</span>
      {entry.note && <span className="italic text-dim">&quot;{entry.note}&quot;</span>}
      <span className="text-xs text-dim">
        {formatRoundTime(entry.dateRange.start)} → {formatRoundTime(entry.dateRange.end)}
      </span>
    </li>
  );
}

/**
 * The session panel's action buttons (reset session, reset account, and
 * ShareButton's Copy result, which mirrors this): bordered like the replay
 * controls so they read as controls rather than a line of prose.
 */
const SESSION_ACTION =
  "flex min-h-8 w-fit items-center rounded border border-muted/30 px-3 py-1 text-muted transition-colors hover:border-accent hover:text-accent sm:min-h-0";

/** "This sitting": rounds, accuracy, streak, Trade P&L, and the session's
 * own start time so the boundary reads plainly rather than being implied. */
function SessionSummaryStrip() {
  const startedAt = useSessionStore((s) => s.startedAt);
  const resetSession = useSessionStore((s) => s.resetSession);
  const summary = useSessionSummary();
  const balance = useAccountStore((s) => s.balance);
  const stats = useAccountStore((s) => s.stats);
  const resetAccount = useAccountStore((s) => s.reset);
  const showAccountReset = stats.trades > 0 || balance !== DEFAULT_BALANCE;

  return (
    <div className="flex flex-col gap-y-2 py-2 text-xs" aria-label="session summary">
      <p className="text-muted">Session started {timeText(startedAt)}.</p>
      {summary.rounds === 0 && summary.practice === 0 ? (
        <p className="text-muted">No rounds played this session yet.</p>
      ) : (
        <dl className="flex flex-wrap gap-x-5 gap-y-1">
          <div>
            <dt className="inline text-muted">rounds </dt>
            <dd className="inline font-bold text-fg">{summary.rounds}</dd>
          </div>
          <div>
            <dt className="inline text-muted">accuracy </dt>
            <dd className="inline font-bold text-fg">
              {summary.accuracy === null
                ? "—"
                : `${Math.round(summary.accuracy * 100)}% (${summary.correct}/${summary.graded})`}
            </dd>
          </div>
          <div>
            <dt className="inline text-muted">streak </dt>
            <dd className="inline font-bold text-fg">{summary.streak}</dd>
          </div>
          {/* Practice replays of an already-recorded challenge are counted
              here and nowhere else (GRA-154): nothing they do is kept, so
              they must not move accuracy or the streak above. */}
          {summary.practice > 0 && (
            <div>
              <dt className="inline text-muted">{RUN_KIND_LABEL.practice.toLowerCase()} </dt>
              <dd className="inline font-bold text-fg">{summary.practice}</dd>
            </div>
          )}
          {summary.trades > 0 && (
            <div>
              <dt className="inline text-muted">trade P&L </dt>
              <dd
                className={`inline font-bold ${
                  summary.pnl > 0 ? "text-up" : summary.pnl < 0 ? "text-down" : "text-fg"
                }`}
              >
                {money(summary.pnl)}
              </dd>
            </div>
          )}
        </dl>
      )}
      {stats.trades > 0 && (
        <div className="flex flex-col gap-y-1">
          <p className="text-muted">Account (all sessions)</p>
          <dl className="flex flex-wrap gap-x-5 gap-y-1">
            <div>
              <dt className="inline text-muted">
                avg win / loss{" "}
              </dt>
              <dd className="inline font-bold text-fg">
                <span className="text-up">{money(avgWin(stats))}</span>
                {" / "}
                <span className="text-down">{money(avgLoss(stats))}</span>
              </dd>
            </div>
            <div>
              <dt className="inline text-muted">
                best win / loss{" "}
              </dt>
              <dd className="inline font-bold text-fg">
                <span className="text-up">{money(stats.biggestWin)}</span>
                {" / "}
                <span className="text-down">{money(stats.biggestLoss)}</span>
              </dd>
            </div>
          </dl>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <button
          type="button"
          onClick={resetSession}
          className={SESSION_ACTION}
        >
          reset session
        </button>
        {showAccountReset && (
          <Tooltip text="reset balance and stats to start over" interactive>
            <button
              type="button"
              onClick={resetAccount}
              className={SESSION_ACTION}
            >
              reset account
            </button>
          </Tooltip>
        )}
        {/* The run's share control (GRA-155): it used to sit at the end of
            the header's StatusBar, one per Loop's own stats row; it lives
            here instead, one control per run rather than the group repeated
            across every Loop's header. */}
        <ShareButton />
      </div>
    </div>
  );
}

/**
 * The two trigger buttons only, meant to sit alongside settings/tools in the
 * shared utility row (LoopShell). Bound to "l" (history log) and "s"
 * (session) the same way settings is bound to ",": ignored while a dialog is
 * open, while typing in a field, or with a modifier key held, so the
 * shortcut never fires alongside a browser or OS chord.
 */
export function HistoryToggles() {
  const { open, summaryOpen, toggle, toggleSummary } = useHistoryPanel();
  const entries = useHistoryStore((s) => s.entries);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (isDialogOpen()) return;
      const el = event.target as HTMLElement | null;
      if (el?.tagName === "INPUT" || el?.tagName === "TEXTAREA") return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      const key = event.key.toLowerCase();
      if (key === "l") {
        event.preventDefault();
        toggle();
      } else if (key === "s") {
        event.preventDefault();
        toggleSummary();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [toggle, toggleSummary]);

  return (
    <>
      <button
        type="button"
        onClick={toggle}
        className="flex min-h-8 items-center text-xs text-muted hover:text-accent sm:min-h-6"
        aria-expanded={open}
      >
        {open ? "▾ history" : "▸ history"}
        <Kbd>L</Kbd>
        {entries.length > 0 && <span className="ml-1 text-dim">({entries.length})</span>}
      </button>
      <button
        type="button"
        onClick={toggleSummary}
        className="flex min-h-8 items-center text-xs text-muted hover:text-accent sm:min-h-6"
        aria-expanded={summaryOpen}
      >
        {summaryOpen ? "▾ session" : "▸ session"}
        <Kbd>S</Kbd>
      </button>
    </>
  );
}

/**
 * What the two toggles above open: rendered as `basis-full` siblings in the
 * same wrapping flex row (matching how the settings panel and the Tools
 * drawer panel wrap onto their own line under that row's triggers), so both
 * still open directly beneath the shared utility row.
 */
export function HistoryPanels() {
  const { open, summaryOpen } = useHistoryPanel();
  const [reviewing, setReviewing] = useState<HistoryEntry | null>(null);
  const entries = useHistoryStore((s) => s.entries);

  return (
    <>
      {/* The book recommendation's session-milestone placement (see
          BookNudge.tsx). It sits here, with the session summary it is keyed
          off, rather than anywhere near the chart or the guess controls: it
          is a footnote to the sitting's numbers, and it renders whether or
          not either panel is open so it does not depend on the player having
          gone looking for it. */}
      <MilestoneBookNudge />

      {summaryOpen && (
        <div className="basis-full">
          <SessionSummaryStrip />
        </div>
      )}

      {open && (
        <div className="basis-full">
          {entries.length === 0 ? (
            <p className="py-2 text-xs text-muted">No rounds played yet.</p>
          ) : (
            // Capped the same way SettingsBar's strip is: tall enough to crush
            // the chart below on a phone otherwise, so it scrolls in place.
            <ul className="max-h-[45vh] overflow-y-auto py-1 text-xs" aria-label="round history">
              {entries.map((entry) => (
                <HistoryRow key={entry.id} entry={entry} onReview={setReviewing} />
              ))}
            </ul>
          )}
        </div>
      )}
      {reviewing && (
        <Suspense fallback={null}>
          <ReviewDialog key={reviewing.id} entry={reviewing} onClose={() => setReviewing(null)} />
        </Suspense>
      )}
    </>
  );
}
